import PropTypes from 'prop-types';
import React, { memo } from 'react';

// Imported directly rather than via the `@/hooks` barrel, which would pull in
// `utils/v2` and cycle back to this tag through `getLevelOfServiceTag`.
import useIsOverflowing from '@/hooks/useIsOverflowing';
import { MedicalServiceIcon } from 'assets/v2/icons';
import Tooltip from 'components/v2/Tooltip';

import { StyledTag, TagLabel } from './LevelOfServiceTag.styles';

const LevelOfServiceTag = ({ label, wraps = false, ...props }) => {
  // Level of service is free text and is routinely wider than the container it
  // renders in, so surface the full value on hover once it starts truncating.
  // `observedValue` re-measures when the label changes in place (e.g. switching
  // trips in the Trip Map detail panel, which never remounts this tag).
  const [isOverflowing, labelRef] = useIsOverflowing({
    enabled: !wraps,
    observedValue: label,
  });

  const tag = (
    <StyledTag
      $wraps={wraps}
      label={
        <TagLabel ref={labelRef} $wraps={wraps}>
          {label}
        </TagLabel>
      }
      icon={<MedicalServiceIcon />}
      {...props}
    />
  );

  // A wrapping tag shows its whole label, so there is nothing for a tooltip to
  // reveal — and inside another tooltip a second one would stack on top of it.
  if (wraps) return tag;

  return (
    <Tooltip
      title={label}
      disableHoverListener={!isOverflowing}
      disableFocusListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
    >
      {tag}
    </Tooltip>
  );
};

LevelOfServiceTag.propTypes = {
  label: PropTypes.string.isRequired,
  // Lets a long label break onto more lines instead of truncating. For places
  // with no room to hover, such as a list already shown in a tooltip.
  wraps: PropTypes.bool,
};

export default memo(LevelOfServiceTag);
